import {
  Children,
  KeyboardEvent,
  ReactNode,
  Dispatch,
  SetStateAction,
  useRef,
  createElement,
  ReactElement,
} from "react";
import styled, { css } from "styled-components/macro";
import { THEME_COLOR, THEME_COLOR_DARK } from "../vars";
import useIsReadonly from "../hooks/use-is-readonly";

export function Segments({
  small = true,
  disabled = false,
  children,
  value = 0,
  onChange,
}: {
  small?: boolean;
  disabled?: boolean;
  children: ReactNode;
  value: any;
  onChange: Dispatch<SetStateAction<any>>;
}) {
  disabled = useIsReadonly() || disabled;

  return (
    <SegmentsContainer>
      {Children.map(children, (child, index) => {
        if (
          child &&
          typeof child === "object" &&
          "type" in child &&
          child.type === Segment
        ) {
          return createElement(SegmentReal, {
            children: child.props.children,
            value: child.props.value,
            disabled,
            small,
            checked: (child.props.value || index) === value,
            onChange: () => onChange(child.props.value || index),
          });
        }
      })}
    </SegmentsContainer>
  );
}

// inspired by https://twitter.com/markdalgleish/status/1370951759284162565/photo/1
export function Segment(_p: {
  value: string;
  children: ReactNode;
}): ReactElement {
  throw new Error("Segment needs to be a child of Segments");
}

function SegmentReal({
  children,
  checked,
  value,
  onChange,
  disabled,
  small,
}: {
  children: ReactNode;
  checked: boolean;
  value: string;
  onChange: Dispatch<SetStateAction<string>>;
  disabled: boolean;
  small: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onKeyPress = (e: KeyboardEvent) => {
    if (e.key !== " ") return;
    e.preventDefault();
    inputRef.current!.click();
  };

  return (
    <SegmentContainer tabIndex={0} onKeyPress={onKeyPress} small={small}>
      <input
        type="radio"
        checked={checked}
        disabled={disabled}
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div>{children}</div>
    </SegmentContainer>
  );
}

const SegmentsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const SegmentContainer = styled.label<{ small: boolean }>`
  display: flex;
  flex: auto;
  font-size: 16px;
  input {
    display: none;
  }
  & > div {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: auto;
    font-weight: 400;
    color: ${THEME_COLOR};
    padding: 8px 0;
    border: 1px solid ${THEME_COLOR};
    background: white;
  }
  &:not(:first-child) > div {
    border-left: none;
  }
  &:first-child > div {
    padding-left: 6px;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  &:last-child > div {
    padding-right: 6px;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  input:disabled + div {
    opacity: 0.5;
    cursor: default;
  }
  input:checked + div {
    color: white;
    background: ${THEME_COLOR};
  }
  &:active > div {
    color: white;
    border-color: ${THEME_COLOR_DARK};
    background: ${THEME_COLOR_DARK};
  }
  ${(p) =>
    p.small &&
    css`
       {
        font-size: 13px;
        line-height: 20px;
        &:first-child > div {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }
        &:last-child > div {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }
    `}
`;
